import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputLabel,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    useCheckDuplicateTaskTemplateName,
    useCopyTaskTemplate,
    useGetTaskTemplateModal,
    useInsertTaskTemplate,
    useSaveAsTaskTemplate,
    useSaveAsTaskTemplateGeneral,
    useUpdateTaskTemplate
} from 'api/taskTemplates';
import CircularWithPath from 'components/@extended/progress/CircularWithPath';
import { useFormik } from 'formik';
import useAuth from 'hooks/useAuth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { mutate } from 'swr';
import { RecordState } from 'types/common';
import { TaskTemplateModalProps } from 'types/PPFUser/taskTemplates';
import { handleApiError, showSuccess } from 'utils/errorHandler';
import { ModernGeneralIcon, ModernIconInspections, ModernIconrPlanReviewer, ModernProjectIcon } from 'utils/icons';
import * as Yup from 'yup';

const TaskTemplateSchema = Yup.object().shape({
    templateName: Yup.string().trim().required('Template name is required').max(200, 'Template name must be at most 200 characters'),
    taskModule: Yup.number().required('Module is required')
});

export const moduleOptions = [
    { taskModule: 0, moduleName: 'General' },
    { taskModule: 1, moduleName: 'Project' },
    { taskModule: 2, moduleName: 'Plan Review' },
    { taskModule: 3, moduleName: 'Inspection' }
];

export type TaskTemplateFormValues = Yup.InferType<typeof TaskTemplateSchema>;

const TaskTemplateModal = ({
    open,
    handleClose,
    taskTemplateID,
    isCopyModal,
    isSaveAsModal,
    taskModule,
    taskIDs
}: TaskTemplateModalProps) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isClosingRef = useRef(false);
    const timerRef = useRef<any>(null);
    const lastCheckedNameRef = useRef<string>('');
    const checkDuplicatePromiseRef = useRef<Promise<boolean> | null>(null);
    const { projectID } = useParams();

    const { data, isLoading, mutate: mutateTaskTemplate } = useGetTaskTemplateModal(taskTemplateID ? taskTemplateID : null);
    const { copyTaskTemplate } = useCopyTaskTemplate();

    const [isDuplicate, setIsDuplicate] = useState(false);
    const [duplicateError, setDuplicateError] = useState(false);

    const checkDuplicate = useCallback(async (name: string) => {
        try {
            setDuplicateError(false);
            lastCheckedNameRef.current = name;

            const promise = useCheckDuplicateTaskTemplateName(name, taskTemplateID ? taskTemplateID : undefined);
            checkDuplicatePromiseRef.current = promise;

            const res = await promise;
            setIsDuplicate(res);
            return res;
        } catch (error) {
            setDuplicateError(true);
            lastCheckedNameRef.current = '';
            return false;
        } finally {
            checkDuplicatePromiseRef.current = null;
        }
    }, []);

    const initialValues = useMemo(() => {
        if (taskTemplateID) {
            return {
                templateName: isCopyModal ? `${data?.templateName}_copy` : data?.templateName,
                taskModule: data?.taskModule ?? 0
            };
        }

        return {
            templateName: '',
            taskModule: taskModule ?? 0
        };
    }, [data, taskTemplateID, isCopyModal]);

    const formik = useFormik<TaskTemplateFormValues>({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: TaskTemplateSchema,
        onSubmit: async (values, { setSubmitting }) => {
            const trimmedVal = values.templateName.trim();

            if (timerRef.current) clearTimeout(timerRef.current);

            let duplicate = isDuplicate;

            if (checkDuplicatePromiseRef.current) {
                duplicate = await checkDuplicatePromiseRef.current;
            } else if (trimmedVal !== lastCheckedNameRef.current) {
                duplicate = await checkDuplicate(trimmedVal);
            }

            if (duplicate) {
                setSubmitting(false);
                return;
            }

            const isGlobal = data?.recordState === RecordState.Global && user?.userType === 2;

            try {
                if (taskTemplateID) {
                    if (isCopyModal) {
                        const result = await copyTaskTemplate({
                            sourceTaskTemplateID: taskTemplateID,
                            newTemplateName: values.templateName
                        });

                        showSuccess('Task template copied successfully');
                        navigate(`/settings/taskTemplates/edit/${result.primaryKeyID}`);
                    } else {
                        const result = await useUpdateTaskTemplate(
                            taskTemplateID,
                            {
                                active: data?.active,
                                templateName: values.templateName,
                                taskModule: values.taskModule,
                                taskTemplateID: taskTemplateID,
                                updatedOn: data?.updatedOn || null
                            },
                            undefined,
                            { disableCacheUpdate: true }
                        );

                        showSuccess('Task template updated successfully');
                        if (isGlobal) {
                            navigate(
                                `/settings/taskTemplates/edit/${result.additionalResponseData?.OverriddenTaskTemplateID ? result.additionalResponseData?.OverriddenTaskTemplateID : result.primaryKeyID}`,
                                { replace: true }
                            );
                        }

                        if (!isGlobal) {
                            mutateTaskTemplate();
                        }
                    }
                } else {
                    if (isSaveAsModal) {
                        if (taskModule === 0) {
                            const payload = {
                                taskTemplateName: values.templateName,
                                taskIDs: taskIDs || []
                            };

                            await useSaveAsTaskTemplateGeneral(payload);
                            showSuccess('Task template saved successfully');
                        } else {
                            const payload = {
                                taskTemplateName: values.templateName,
                                taskModule: values.taskModule
                            };

                            await useSaveAsTaskTemplate(Number(projectID), payload)
                            showSuccess('Task template saved successfully');
                        }

                        if (location.pathname === '/settings/taskTemplates') {
                            mutate([`TaskTemplate/GetList`, { method: 'GET' }]);

                        }

                        handleClose();
                    } else {
                        const result = await useInsertTaskTemplate({
                            active: true,
                            templateName: values.templateName,
                            taskModule: values.taskModule
                        });

                        showSuccess('Task template created successfully');
                        navigate(`/settings/taskTemplates/edit/${result.primaryKeyID}`);
                    }
                }

                handleClose();
            } catch (error) {
                handleApiError(error);
            } finally {
                setSubmitting(false);
            }
        }
    });

    const { values, errors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue } = formik;

    useEffect(() => {
        const trimmedVal = values.templateName.trim();
        const originalVal = initialValues.templateName?.trim();

        if (!trimmedVal || trimmedVal === originalVal) {
            setIsDuplicate(false);
            return;
        }

        if (trimmedVal !== lastCheckedNameRef.current) {
            setIsDuplicate(false);
            setDuplicateError(false);
            lastCheckedNameRef.current = '';
        }

        if (timerRef.current) clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
            checkDuplicate(trimmedVal);
        }, 3000);

        return () => clearTimeout(timerRef.current);
    }, [values.templateName, checkDuplicate]);

    const getNameError = useCallback(() => {
        if (touched.templateName && errors.templateName) {
            return errors.templateName;
        }

        if (isDuplicate) {
            return 'Template name already exists';
        }

        // Show error if API call failed
        if (duplicateError) {
            return 'Error checking template name availability';
        }

        return '';
    }, [touched.templateName, errors.templateName, isDuplicate, duplicateError]);

    const isNameError = Boolean(
        (touched.templateName && errors.templateName) || (values.templateName && isDuplicate) || (values.templateName && duplicateError)
    );

    const isDirty = (data?.templateName ?? '') !== values.templateName || (data?.taskModule ?? 0) !== values?.taskModule;

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    handleClose();
                }
            }}
            fullWidth
            slotProps={{ paper: { sx: { maxWidth: 766 } } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} component={Box}>
                <Typography variant="h6">
                    <Typography component="span" color="primary">
                        {isCopyModal ? 'Copy' : taskTemplateID ? 'Edit' : 'New'}
                    </Typography>{' '}
                    <Typography component="span">Template</Typography>
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        isClosingRef.current = true;
                        handleClose();
                    }}
                    color="primary"
                >
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <Divider />
            {isLoading ? (
                <Box sx={{ p: 5 }}>
                    <Stack direction="row" sx={{ justifyContent: 'center' }}>
                        <CircularWithPath />
                    </Stack>
                </Box>
            ) : (
                <DialogContent sx={{ p: 3 }}>
                    <Stack spacing={0.75}>
                        <InputLabel required>Template Name</InputLabel>
                        <TextField
                            name="templateName"
                            fullWidth
                            autoFocus
                            value={values.templateName}
                            onChange={handleChange}
                            placeholder="Enter Template Name"
                            onBlur={(e) => {
                                if (isClosingRef.current) return;

                                formik.handleBlur(e);
                                const trimmedVal = e.target.value.trim();
                                if (trimmedVal && trimmedVal !== lastCheckedNameRef.current) {
                                    if (timerRef.current) clearTimeout(timerRef.current);
                                    checkDuplicate(trimmedVal);
                                }
                            }}
                            error={isNameError}
                            slotProps={{ htmlInput: { maxLength: 200 } }}
                            helperText={getNameError()}
                        />
                    </Stack>
                    <Stack spacing={0.75} mt={3}>
                        <InputLabel required>Module</InputLabel>
                        <Autocomplete
                            id="task-module-autocomplete"
                            options={moduleOptions}
                            disableClearable={true as boolean}
                            value={moduleOptions.find((option) => option.taskModule === values.taskModule) ?? null}
                            disabled={isCopyModal || !!taskTemplateID || isSaveAsModal}
                            onChange={(_, newValue) => {
                                setFieldValue('taskModule', newValue ? newValue.taskModule : null);
                            }}
                            getOptionLabel={(option) => option.moduleName || ''}
                            isOptionEqualToValue={(option, val) => option.taskModule === (typeof val === 'string' ? val : val?.taskModule)}
                            renderOption={(props, option) => {
                                const { key, ...restProps } = props as any;
                                return (
                                    <Box component="li" key={key || option.taskModule} {...restProps}>
                                        <Stack direction="row" spacing="6px" alignItems="center">
                                            {option.taskModule === 2 && <ModernIconrPlanReviewer style={{ height: 13.5, width: 13.5 }} />}
                                            {option.taskModule === 3 && <ModernIconInspections style={{ height: 16, width: 16 }} />}
                                            {option.taskModule === 1 && <ModernProjectIcon />}
                                            {option.taskModule === 0 && <ModernGeneralIcon />}
                                            <Typography>{option.moduleName}</Typography>
                                        </Stack>
                                    </Box>
                                );
                            }}
                            renderInput={(params) => {
                                const selectedOpt = moduleOptions.find((option) => option.taskModule === values.taskModule);
                                return (
                                    <TextField
                                        {...params}
                                        placeholder="Select Module"
                                        error={Boolean(touched.taskModule && errors.taskModule)}
                                        helperText={touched.taskModule ? (errors.taskModule as string) : undefined}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        {selectedOpt && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, mr: 0.5 }}>
                                                                {selectedOpt.taskModule === 2 && <ModernIconrPlanReviewer style={{ height: 13.5, width: 13.5 }} />}
                                                                {selectedOpt.taskModule === 3 && <ModernIconInspections style={{ height: 16, width: 16 }} />}
                                                                {selectedOpt.taskModule === 1 && <ModernProjectIcon />}
                                                                {selectedOpt.taskModule === 0 && <ModernGeneralIcon />}
                                                            </Box>
                                                        )}
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                )
                                            }
                                        }}
                                    />
                                );
                            }}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
            )}

            <DialogActions sx={{ padding: 3 }}>
                <Button
                    variant="contained"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting || isDuplicate || !isDirty}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
                >
                    {isCopyModal ? 'Copy Template' : taskTemplateID ? 'Save' : 'Create Template'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskTemplateModal;
